/**
 * Step 2: Asset & Network Selector
 * 
 * User selects which cryptocurrency to accept and on which network.
 * Shows visual cards for assets and chips for networks.
 * Handles asset-network compatibility automatically.
 * 
 * @module addresses/wizard
 */

import { NetworkIcon } from '@/components/NetworkIcons';
import { Badge } from '@/components/ui/badge';
import { CurrencyIcon } from '@/components/ui/CurrencyIcon';
import { TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger } from '@/components/ui/tooltip';
import { Heading } from "@/components/ui/typography";
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import React, { useMemo } from 'react';
import {
  type BlockchainNetwork,
  type CryptoAsset,
  ASSET_INFO,
  ASSET_NETWORK_COMPATIBILITY,
  NETWORK_LABELS,
} from './types';

interface AssetSelectorProps {
  selectedAsset: CryptoAsset;
  selectedNetwork: BlockchainNetwork;
  onAssetChange: (asset: CryptoAsset) => void;
  onNetworkChange: (network: BlockchainNetwork) => void;
}

// Popular assets shown first (only assets with available icons)
const POPULAR_ASSETS: CryptoAsset[] = ['USDC', 'USDT'];
// Note: USDB, USDP excluded - missing crypto-icons
const ALL_ASSETS: CryptoAsset[] = ['USDC', 'USDT', 'EURC', 'PYUSD', 'DAI', 'ETH'];

export const AssetSelector: React.FC<AssetSelectorProps> = ({
  selectedAsset,
  selectedNetwork,
  onAssetChange,
  onNetworkChange,
}) => {
  // Get available networks for selected asset
  const availableNetworks = useMemo(() => {
    return ASSET_NETWORK_COMPATIBILITY[selectedAsset] || [];
  }, [selectedAsset]);

  // Auto-select first available network if current is not compatible
  React.useEffect(() => {
    if (!availableNetworks.includes(selectedNetwork) && availableNetworks.length > 0) {
      onNetworkChange(availableNetworks[0]);
    }
  }, [selectedAsset, availableNetworks, selectedNetwork, onNetworkChange]);

  // Get network recommendation
  const getNetworkRecommendation = (network: BlockchainNetwork): string | null => {
    if (network === 'polygon' && selectedAsset === 'USDC') {
      return 'Lower fees';
    }
    if (network === 'ethereum') {
      return 'Most liquid';
    }
    return null;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-2">
        <Heading level={2} className={cn("text-xl sm:text-heading-lg", semanticTokens.fg.default)}>
          Which cryptocurrency do you want to accept?
        </Heading>
        <p className={cn("text-sm sm:text-base", semanticTokens.fg.muted)}>
          Select an asset and network for your address
        </p>
      </div>

      {/* Asset Selection */}
      <div className="space-y-4">
        <h3 className={cn("text-sm font-medium", semanticTokens.fg.muted)}>
          Select Asset
        </h3>

        {/* Popular Assets Grid */}
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {ALL_ASSETS.map((asset) => {
            const isSelected = selectedAsset === asset;
            const isPopular = POPULAR_ASSETS.includes(asset);
            const info = ASSET_INFO[asset];
            const networkCount = ASSET_NETWORK_COMPATIBILITY[asset]?.length || 0;

            return (
              <TooltipProvider key={asset}>
                <TooltipRoot>
                  <TooltipTrigger
                    type="button"
                    onClick={() => onAssetChange(asset)}
                    className={cn(
                      "relative p-3 sm:p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all duration-240",
                      "hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                      "min-h-[80px] sm:min-h-[100px] w-full",
                      isSelected
                        ? "border-primary bg-primary/5"
                        : cn(semanticTokens.border.default, semanticTokens.bg.surface, "hover:border-primary/40")
                    )}
                    aria-pressed={isSelected}
                  >
                    {/* Popular badge */}
                    {isPopular && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full" />
                    )}

                    {/* Icon */}
                    <CurrencyIcon symbol={asset} size="md" />

                    {/* Label */}
                    <span className={cn(
                      "text-sm font-medium",
                      isSelected ? "text-primary" : semanticTokens.fg.default
                    )}>
                      {asset}
                    </span>

                    {/* Network count */}
                    <span className={cn("text-xs", semanticTokens.fg.muted)}>
                      {networkCount} {networkCount === 1 ? 'network' : 'networks'}
                    </span>

                    {/* Selected checkmark */}
                    {isSelected && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                        <Check className="h-3 w-3 text-primary-foreground" />
                      </div>
                    )}
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-medium">{info.name}</p>
                    <p className={cn("text-xs", semanticTokens.fg.disabled)}>{info.description}</p>
                  </TooltipContent>
                </TooltipRoot>
              </TooltipProvider>
            );
          })}
        </div>
      </div>

      {/* Network Selection */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className={cn("text-sm font-medium", semanticTokens.fg.muted)}>
            Select Network
          </h3>
          <span className={cn("text-xs", semanticTokens.fg.subtle)}>
            {availableNetworks.length} available for {selectedAsset}
          </span>
        </div>

        {/* Network Chips */}
        <div className="flex flex-wrap gap-2">
          {availableNetworks.map((network) => {
            const isSelected = selectedNetwork === network;
            const recommendation = getNetworkRecommendation(network);

            return (
              <button
                key={network}
                type="button"
                onClick={() => onNetworkChange(network)}
                className={cn(
                  "inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition-all duration-240",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                  isSelected
                    ? "border-primary bg-primary/5"
                    : cn(semanticTokens.border.default, semanticTokens.bg.surface, "hover:border-primary/40")
                )}
                aria-pressed={isSelected}
              >
                <NetworkIcon network={network} className="w-5 h-5" />
                <span className={cn(
                  "text-sm font-medium",
                  isSelected ? "text-primary" : semanticTokens.fg.default
                )}>
                  {NETWORK_LABELS[network]}
                </span>
                {recommendation && (
                  <Badge
                    variant={isSelected ? "default" : "secondary"}
                    className="text-xs py-0"
                  >
                    {recommendation}
                  </Badge>
                )}
                {isSelected && (
                  <SFIcon icon={sfIcons.check} size="sm" className="text-primary" />
                )}
              </button>
            );
          })}
        </div>

        {/* No networks available */}
        {availableNetworks.length === 0 && (
          <div className={cn(
            "p-4 rounded-lg text-center",
            semanticTokens.warning.bg
          )}>
            <p className={cn("text-sm", semanticTokens.warning.fg)}>
              No networks available for {selectedAsset}. Please select a different asset.
            </p>
          </div>
        )}
      </div>

      {/* Selection Summary */}
      <div className={cn(
        "p-4 rounded-lg border",
        semanticTokens.bg.muted,
        semanticTokens.border.default
      )}>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <CurrencyIcon symbol={selectedAsset} size="sm" />
            <span className={cn("font-medium", semanticTokens.fg.default)}>
              {selectedAsset}
            </span>
          </div>
          <span className={semanticTokens.fg.muted}>on</span>
          <div className="flex items-center gap-2">
            <NetworkIcon network={selectedNetwork} className="w-4 h-4" />
            <span className={cn("font-medium", semanticTokens.fg.default)}>
              {NETWORK_LABELS[selectedNetwork]}
            </span>
          </div>
        </div>

        <div className={cn("mt-3 flex items-start gap-2 text-xs", semanticTokens.fg.muted)}>
          <SFIcon icon={sfIcons.info} size="sm" className="shrink-0 mt-0.5" />
          <span>
            When someone sends {selectedAsset} to your address on {NETWORK_LABELS[selectedNetwork]},
            it will be automatically processed.
          </span>
        </div>
      </div>
    </div>
  );
};

export default AssetSelector;

